(() => {
  if (window.eTrendCardAdminSync) {
    window.eTrendCardAdminSync.capture();
    window.eTrendCardAdminSync.schedule();
    return;
  }

  const settingsEvent = 'e-trend:card-settings';
  const settingsSelector = '#ProductGridContainer [data-e-trend-card-settings]';
  const fieldLabels = {
    storage: 'storageLabel',
    memory: 'memoryLabel',
    display: 'displayLabel',
    keyboard: 'keyboardLabel',
    warranty: 'warrantyLabel'
  };
  let activeSettings = null;
  let animationFrame = 0;

  function booleanValue(value, fallback) {
    return typeof value === 'boolean' ? value : fallback;
  }

  function textValue(value, fallback) {
    return typeof value === 'string' && value.trim() ? value.trim() : fallback;
  }

  function numberValue(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  }

  function normalize(settings) {
    settings = settings || {};
    const legacyShowStorage = booleanValue(settings.showStorage, true);
    const legacyShowMemory = booleanValue(settings.showMemory, true);
    const showStorageLaptops = booleanValue(settings.showStorageLaptops, legacyShowStorage);
    const showMemoryLaptops = booleanValue(settings.showMemoryLaptops, legacyShowMemory);
    const showStorageNotebooks = booleanValue(settings.showStorageNotebooks, legacyShowStorage);
    const showMemoryNotebooks = booleanValue(settings.showMemoryNotebooks, legacyShowMemory);

    return {
      enabled: booleanValue(settings.enabled, true),
      showGrade: booleanValue(settings.showGrade, true),
      showStorage: showStorageLaptops || showStorageNotebooks,
      showMemory: showMemoryLaptops || showMemoryNotebooks,
      showStorageLaptops,
      showMemoryLaptops,
      showStorageNotebooks,
      showMemoryNotebooks,
      showDisplay: booleanValue(settings.showDisplay, true),
      showKeyboard: booleanValue(settings.showKeyboard, true),
      showWarranty: booleanValue(settings.showWarranty, true),
      showCartButton: booleanValue(settings.showCartButton, false),
      storageLabel: textValue(settings.storageLabel, 'Opslag'),
      memoryLabel: textValue(settings.memoryLabel, 'Geheugen'),
      displayLabel: textValue(settings.displayLabel, 'Display'),
      keyboardLabel: textValue(settings.keyboardLabel, 'Toetsenbord'),
      warrantyLabel: textValue(settings.warrantyLabel, 'Garantie'),
      desktopMinHeight: numberValue(settings.desktopMinHeight, 150),
      mobileMinHeight: numberValue(settings.mobileMinHeight, 210),
      cartButtonText: textValue(settings.cartButtonText, 'In winkelwagen'),
      cartSoldOutText: textValue(settings.cartSoldOutText, 'Uitverkocht')
    };
  }

  function setRootFlags(settings) {
    const flags = {
      'data-e-trend-highlights-enabled': settings.enabled,
      'data-e-trend-show-grade': settings.showGrade,
      'data-e-trend-show-storage': settings.showStorage,
      'data-e-trend-show-memory': settings.showMemory,
      'data-e-trend-show-storage-laptops': settings.showStorageLaptops,
      'data-e-trend-show-memory-laptops': settings.showMemoryLaptops,
      'data-e-trend-show-storage-notebooks': settings.showStorageNotebooks,
      'data-e-trend-show-memory-notebooks': settings.showMemoryNotebooks,
      'data-e-trend-show-display': settings.showDisplay,
      'data-e-trend-show-keyboard': settings.showKeyboard,
      'data-e-trend-show-warranty': settings.showWarranty,
      'data-e-trend-show-cart-button': settings.showCartButton
    };

    Object.entries(flags).forEach(([name, value]) => {
      document.documentElement.setAttribute(name, value ? 'true' : 'false');
    });
  }

  function apply() {
    animationFrame = 0;
    if (!activeSettings) return;

    const scope = document.querySelector('#ProductGridContainer');
    if (!scope) return;

    setRootFlags(activeSettings);

    scope.querySelectorAll('.e-trend-card-highlights').forEach((element) => {
      element.style.setProperty('--e-trend-card-height-desktop', `${activeSettings.desktopMinHeight}px`);
      element.style.setProperty('--e-trend-card-height-mobile', `${activeSettings.mobileMinHeight}px`);
    });

    Object.entries(fieldLabels).forEach(([field, settingName]) => {
      scope.querySelectorAll(`[data-e-trend-highlight-field="${field}"] [data-e-trend-highlight-label]`).forEach((label) => {
        const nextText = activeSettings[settingName];
        if (label.textContent !== nextText) label.textContent = nextText;
      });
    });

    const feedSettings = {
      storage: {
        notebooks: 'showStorageNotebooks',
        laptops: 'showStorageLaptops'
      },
      memory: {
        notebooks: 'showMemoryNotebooks',
        laptops: 'showMemoryLaptops'
      }
    };

    Object.entries(feedSettings).forEach(([field, sourceSettings]) => {
      scope.querySelectorAll('.e-trend-card-highlights').forEach((highlights) => {
        const rows = Array.from(highlights.querySelectorAll(`[data-e-trend-highlight-field="${field}"]`));
        const selected = rows.find((row) => {
          const source = row.getAttribute('data-e-trend-highlight-source');
          const settingName = sourceSettings[source];
          return settingName ? activeSettings[settingName] : activeSettings[field === 'storage' ? 'showStorage' : 'showMemory'];
        });

        rows.forEach((row) => {
          row.classList.toggle('e-trend-card-source-hidden', row !== selected);
        });
      });
    });

    scope.querySelectorAll('[data-e-trend-card-cart]').forEach((cart) => {
      const button = cart.querySelector('.e-trend-card-cart__button');
      const text = cart.querySelector('[data-e-trend-card-cart-text]');
      if (!button || !text) return;

      const soldOut = text.hasAttribute('data-e-trend-card-cart-sold-out') || button.disabled;
      const nextText = soldOut ? activeSettings.cartSoldOutText : activeSettings.cartButtonText;
      if (text.textContent !== nextText) text.textContent = nextText;

      if (!soldOut) {
        const title = cart.closest('.product-grid__item')?.querySelector('.card__heading')?.textContent.trim();
        button.setAttribute('aria-label', title ? `${nextText}: ${title}` : nextText);
      }
    });

    if (window.eTrendCardRowAlignment) window.eTrendCardRowAlignment.schedule();
  }

  function schedule() {
    if (animationFrame) cancelAnimationFrame(animationFrame);
    animationFrame = requestAnimationFrame(() => requestAnimationFrame(apply));
  }

  function setSettings(settings) {
    activeSettings = normalize(settings);
    window.eTrendCollectionCardSettings = activeSettings;
    setRootFlags(activeSettings);
    schedule();
  }

  function findSettingsNode(root) {
    if (!root) return document.querySelector(settingsSelector);
    if (root.nodeType === 1 && root.matches('[data-e-trend-card-settings]') && root.closest('#ProductGridContainer')) {
      return root;
    }
    return root.querySelector ? root.querySelector(settingsSelector) || root.querySelector('[data-e-trend-card-settings]') : null;
  }

  function capture(root) {
    const source = findSettingsNode(root || document);
    if (!source || !source.closest('#ProductGridContainer')) return false;

    try {
      setSettings(JSON.parse(source.textContent));
      return true;
    } catch (error) {
      console.error('E-TREND card settings could not be applied.', error);
      return false;
    }
  }

  window.eTrendCardAdminSync = { apply, capture, schedule, setSettings };

  document.addEventListener(settingsEvent, (event) => setSettings(event.detail));
  document.addEventListener('shopify:section:load', (event) => {
    capture(event.target);
    schedule();
  });
  document.addEventListener('shopify:section:reorder', schedule);

  const mutationRoot = document.querySelector('main') || document.body;
  new MutationObserver((mutations) => {
    mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => capture(node)));
    schedule();
  }).observe(mutationRoot, { childList: true, subtree: true });

  if (window.eTrendCollectionCardSettings) setSettings(window.eTrendCollectionCardSettings);
  capture();
  schedule();
})();

(() => {
  if (window.eTrendCardRowAlignment) {
    window.eTrendCardRowAlignment.schedule();
    return;
  }

  let animationFrame = 0;
  let observedGrid = null;

  const resizeObserver = typeof ResizeObserver === 'function'
    ? new ResizeObserver(() => schedule())
    : null;

  function getPriceBlock(item) {
    return Array.from(item.querySelectorAll('.card-product-information > .card-information')).find((element) =>
      element.querySelector('.price')
    );
  }

  function observeCurrentGrid() {
    const grid = document.querySelector('.template-collection .product-grid');
    if (grid === observedGrid) return;

    if (resizeObserver) resizeObserver.disconnect();
    observedGrid = grid;
    if (resizeObserver && observedGrid) resizeObserver.observe(observedGrid);
  }

  function alignRows() {
    animationFrame = 0;
    observeCurrentGrid();

    const cards = Array.from(document.querySelectorAll('.template-collection .product-grid__item'))
      .filter((item) => item.offsetParent !== null)
      .map((item) => ({ item, price: getPriceBlock(item) }))
      .filter((card) => card.price);

    cards.forEach(({ price }) => {
      price.style.removeProperty('--e-trend-row-price-offset');
      price.style.removeProperty('--e-trend-row-price-height');
    });

    // Measure only after every old row offset has been removed. Using viewport
    // rectangles keeps the calculation correct through nested grid/flex layouts,
    // transforms, and the different wrappers used by native and Globo cards.
    void document.documentElement.offsetHeight;

    const rows = [];

    cards.forEach(({ item, price }) => {
      const itemRect = item.getBoundingClientRect();
      const itemTop = itemRect.top;
      let row = rows.find((candidate) => Math.abs(candidate.top - itemTop) <= 4);

      if (!row) {
        row = { top: itemTop, cards: [] };
        rows.push(row);
      }

      const priceRect = price.getBoundingClientRect();
      row.cards.push({ price, priceTop: priceRect.top - itemTop, priceHeight: priceRect.height });
    });

    rows.forEach((row) => {
      const targetTop = Math.max(...row.cards.map((card) => card.priceTop));
      const targetHeight = Math.max(...row.cards.map((card) => card.priceHeight));

      row.cards.forEach((card) => {
        const offset = Math.max(0, targetTop - card.priceTop);
        card.price.style.setProperty('--e-trend-row-price-offset', `${offset}px`);
        card.price.style.setProperty('--e-trend-row-price-height', `${targetHeight}px`);
      });
    });
  }

  function schedule() {
    if (animationFrame) cancelAnimationFrame(animationFrame);
    animationFrame = requestAnimationFrame(() => requestAnimationFrame(alignRows));
  }

  window.eTrendCardRowAlignment = { align: alignRows, schedule };

  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('orientationchange', schedule, { passive: true });
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:section:reorder', schedule);

  const mutationRoot = document.querySelector('main') || document.body;
  new MutationObserver(schedule).observe(mutationRoot, { childList: true, subtree: true });

  if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }
})();
